import * as React from 'react';
import Tooltip from 'rc-tooltip';
import { makeStyles, Theme, withStyles } from '@material-ui/core';
import ImageIcon from '../Icon/ImageIcon.component';
import I18n from '../i18n/I18n.component';
import iconsMap from '../../../config/icons';
import colors from '../../../config/colors';
import { Color } from '../../../interfaces/core.interface';
import 'rc-tooltip/assets/bootstrap_white.css';

type ExtraInfoPropsType = {
	infoText: string | string[];
	mode?: 'big';
	iconName?: keyof typeof iconsMap;
	/** overrides the size 'mode' gives the icon */
	iconSize?: number;
	iconColor?: string;
	/** merged over the default icon margins */
	iconStyle?: any;
	/** merged over the default tooltip overlay style */
	overlayStyle?: React.CSSProperties;
	/** 'card' - borderless white bubble with a shadow, instead of the bootstrap look */
	variant?: 'card';
	textColor?: Color;
	/** rc-tooltip placement, defaults to 'right' */
	placement?: string;
};

const useStylesExtraInfo = makeStyles((theme: Theme) => ({
	arrow: {
		color: theme.palette.common.black,
	},
	tooltip: {
		backgroundColor: theme.palette.common.white,
		color: 'rgba(0, 0, 0, 0.87)',
		boxShadow: theme.shadows[1],
	},
	// overrides the bootstrap_white theme: no border, rounder, shadowed, white arrow
	cardOverlay: {
		opacity: 1,
		padding: 0,
		backgroundColor: 'transparent',

		'& .rc-tooltip-inner': {
			padding: '10px 16px',
			minHeight: 'auto',
			border: 'none',
			borderRadius: 8,
			backgroundColor: colors.white,
			boxShadow: '0 2px 12px 0 rgba(40, 44, 111, 0.16)',
			textAlign: 'right',
		},
		'&.rc-tooltip-placement-top .rc-tooltip-arrow, &.rc-tooltip-placement-topLeft .rc-tooltip-arrow, &.rc-tooltip-placement-topRight .rc-tooltip-arrow':
			{ borderTopColor: colors.white },
		'&.rc-tooltip-placement-bottom .rc-tooltip-arrow, &.rc-tooltip-placement-bottomLeft .rc-tooltip-arrow, &.rc-tooltip-placement-bottomRight .rc-tooltip-arrow':
			{ borderBottomColor: colors.white },
		'&.rc-tooltip-placement-left .rc-tooltip-arrow, &.rc-tooltip-placement-leftTop .rc-tooltip-arrow, &.rc-tooltip-placement-leftBottom .rc-tooltip-arrow':
			{ borderLeftColor: colors.white },
		'&.rc-tooltip-placement-right .rc-tooltip-arrow, &.rc-tooltip-placement-rightTop .rc-tooltip-arrow, &.rc-tooltip-placement-rightBottom .rc-tooltip-arrow':
			{ borderRightColor: colors.white },
	},
}));

const InfoTextComponent = ({ infoText, textColor }: ExtraInfoPropsType) => {
	return (
		<div
			style={{
				textAlign: 'right',
				justifyContent: 'right',
				alignItems: 'right',
			}}
		>
			{Array.isArray(infoText) ? (
				infoText?.map(el => {
					return (
						<I18n key={el} size={14} weight={'normal'} color={textColor}>
							{el}
						</I18n>
					);
				})
			) : (
				<I18n size={14} weight={'normal'} color={textColor}>
					{infoText}
				</I18n>
			)}
		</div>
	);
};

const ExtraInfo = ({
	infoText,
	mode,
	iconName,
	iconSize,
	iconColor,
	iconStyle,
	overlayStyle,
	variant,
	textColor,
	placement,
}: ExtraInfoPropsType) => {
	const classes = useStylesExtraInfo();
	const size = iconSize || (mode === 'big' ? 20 : 14);
	const isCard = variant === 'card';

	return (
		<Tooltip
			trigger={['hover', 'click']}
			placement={placement}
			overlayClassName={isCard ? classes.cardOverlay : undefined}
			overlay={
				<InfoTextComponent
					infoText={infoText}
					textColor={textColor || (isCard ? 'clockRecordBlue' : undefined)}
				/>
			}
			overlayStyle={{ zIndex: 9999, maxWidth: 300, ...overlayStyle }}
		>
			<a>
				<ImageIcon
					name={iconName || 'extraInfo'}
					width={size}
					height={size}
					color={iconColor}
					style={{ marginRight: 5, marginLeft: 5, ...iconStyle }}
				/>
			</a>
		</Tooltip>
	);
};

export default ExtraInfo;
